import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Edit2, Save, X, Plus, Trash2, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import AppHeader from '../components/AppHeader';

const MATRIX_TYPES = [
  { type: 'screen_print', name: 'Screen Printing', icon: '🖨️', color: '#e74c3c' },
  { type: 'embroidery', name: 'Embroidery', icon: '🧵', color: '#3b82f6' },
  { type: 'sublimation', name: 'Sublimation', icon: '🌈', color: '#8b5cf6' },
  { type: 'dtf', name: 'DTF', icon: '📋', color: '#10b981' }
];

const PriceMatrices = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMatrix, setActiveMatrix] = useState('screen_print');
  const [editingMatrix, setEditingMatrix] = useState(null);
  const [expandedTiers, setExpandedTiers] = useState({});

  useEffect(() => {
    fetchMatrices();
  }, []);

  const fetchMatrices = async () => {
    try {
      const response = await axios.get('/price-matrices');
      setMatrices(response.data || []);
    } catch (error) {
      console.error('Failed to load price matrices:', error);
      toast.error('Failed to load price matrices');
    } finally {
      setLoading(false);
    }
  };

  const getMatrixByType = (type) => {
    return matrices.find(m => m.type === type);
  };

  const currentMatrix = getMatrixByType(activeMatrix);
  const matrixConfig = MATRIX_TYPES.find(m => m.type === activeMatrix);

  const toggleTierExpand = (index) => {
    setExpandedTiers(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const startEditing = () => {
    setEditingMatrix(JSON.parse(JSON.stringify(currentMatrix)));
  };

  const cancelEditing = () => {
    setEditingMatrix(null);
  };

  const saveMatrix = async () => {
    try {
      await axios.put(`/price-matrices/${editingMatrix.type}`, {
        name: editingMatrix.name,
        description: editingMatrix.description,
        tiers: editingMatrix.tiers,
        setup_fees: editingMatrix.setup_fees,
        additional_location_fees: editingMatrix.additional_location_fees,
        additional_charges: editingMatrix.additional_charges,
        notes: editingMatrix.notes
      });
      toast.success('Price matrix updated successfully');
      setEditingMatrix(null);
      fetchMatrices();
    } catch (error) {
      toast.error('Failed to update price matrix');
    }
  };

  const updateTierPrice = (tierIndex, priceKey, value) => {
    const newMatrix = { ...editingMatrix };
    newMatrix.tiers[tierIndex].prices[priceKey] = parseFloat(value) || 0;
    setEditingMatrix(newMatrix);
  };

  const updateTierRange = (tierIndex, field, value) => {
    const newMatrix = { ...editingMatrix };
    newMatrix.tiers[tierIndex][field] = parseInt(value) || 0;
    setEditingMatrix(newMatrix);
  };

  const addTier = () => {
    const newMatrix = { ...editingMatrix };
    const lastTier = newMatrix.tiers[newMatrix.tiers.length - 1];
    const newTier = {
      min_qty: (lastTier?.max_qty || 0) + 1,
      max_qty: (lastTier?.max_qty || 0) + 100,
      prices: { ...lastTier?.prices } || {}
    };
    newMatrix.tiers.push(newTier);
    setEditingMatrix(newMatrix);
  };

  const removeTier = (tierIndex) => {
    const newMatrix = { ...editingMatrix };
    newMatrix.tiers.splice(tierIndex, 1);
    setEditingMatrix(newMatrix);
  };

  const updateSetupFee = (key, value) => {
    const newMatrix = { ...editingMatrix };
    newMatrix.setup_fees[key] = parseFloat(value) || 0;
    setEditingMatrix(newMatrix);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const renderPriceTable = (matrix, isEditing = false) => {
    if (!matrix?.tiers?.length) return <p>No pricing tiers defined.</p>;

    const allPriceKeys = [...new Set(matrix.tiers.flatMap(t => Object.keys(t.prices || {})))];
    
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Quantity Range</th>
              {allPriceKeys.map(key => (
                <th key={key} style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                  {key.replace(/_/g, ' ').replace(/(\d+)/g, ' $1 ').trim()}
                </th>
              ))}
              {isEditing && <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(isEditing ? editingMatrix : matrix).tiers.map((tier, tierIndex) => (
              <tr key={tierIndex} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '10px 12px', fontWeight: '500' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Input
                        type="number"
                        value={tier.min_qty}
                        onChange={(e) => updateTierRange(tierIndex, 'min_qty', e.target.value)}
                        style={{ width: '70px', padding: '4px 8px' }}
                      />
                      <span>-</span>
                      <Input
                        type="number"
                        value={tier.max_qty}
                        onChange={(e) => updateTierRange(tierIndex, 'max_qty', e.target.value)}
                        style={{ width: '70px', padding: '4px 8px' }}
                      />
                    </div>
                  ) : (
                    `${tier.min_qty} - ${tier.max_qty}`
                  )}
                </td>
                {allPriceKeys.map(key => (
                  <td key={key} style={{ padding: '10px 12px', textAlign: 'center' }}>
                    {isEditing ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={tier.prices?.[key] || ''}
                        onChange={(e) => updateTierPrice(tierIndex, key, e.target.value)}
                        style={{ width: '80px', padding: '4px 8px', textAlign: 'center' }}
                      />
                    ) : (
                      <span style={{ color: matrixConfig?.color || '#374151' }}>
                        {formatCurrency(tier.prices?.[key])}
                      </span>
                    )}
                  </td>
                ))}
                {isEditing && (
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTier(tierIndex)}
                      style={{ color: '#ef4444', padding: '4px' }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {isEditing && (
          <Button variant="outline" onClick={addTier} style={{ marginTop: '12px' }}>
            <Plus size={16} /> Add Tier
          </Button>
        )}
      </div>
    );
  };

  const renderSetupFees = (matrix, isEditing = false) => {
    const fees = isEditing ? editingMatrix?.setup_fees : matrix?.setup_fees;
    if (!fees || Object.keys(fees).length === 0) return <p style={{ color: '#6b7280' }}>No setup fees defined.</p>;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
        {Object.entries(fees).map(([key, value]) => (
          <div key={key} style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
            <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', textTransform: 'capitalize' }}>
              {key.replace(/_/g, ' ')}
            </p>
            {isEditing ? (
              <Input
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => updateSetupFee(key, e.target.value)}
                style={{ width: '100px' }}
              />
            ) : (
              <p style={{ fontSize: '18px', fontWeight: '700', color: matrixConfig?.color }}>
                {formatCurrency(value)}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderAdditionalCharges = (matrix) => {
    const charges = matrix?.additional_charges;
    if (!charges || Object.keys(charges).length === 0) return null;

    return (
      <div style={{ marginTop: '24px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
          Additional Charges
        </h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {Object.entries(charges).map(([key, value]) => (
            <div key={key} style={{ 
              background: '#fef3c7', 
              padding: '8px 12px', 
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              <span style={{ textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}: </span>
              <strong>{formatCurrency(value)}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard" data-testid="price-matrices-page">
      {/* Header */}
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 className="dashboard-title" style={{ marginBottom: '4px' }}>Price Matrices</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Manage pricing for different print methods</p>
          </div>
        </div>

        {/* Matrix Type Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          marginBottom: '24px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '12px'
        }}>
          {MATRIX_TYPES.map(mt => (
            <button
              key={mt.type}
              onClick={() => { setActiveMatrix(mt.type); setEditingMatrix(null); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                background: activeMatrix === mt.type ? mt.color : '#f3f4f6',
                color: activeMatrix === mt.type ? 'white' : '#374151',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              data-testid={`tab-${mt.type}`}
            >
              <span style={{ fontSize: '18px' }}>{mt.icon}</span>
              {mt.name}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>Loading price matrices...</div>
        ) : !currentMatrix ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280', background: 'white', borderRadius: '12px' }}>
            <DollarSign size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <p>No price matrix found for {matrixConfig?.name}.</p>
          </div>
        ) : (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            borderTop: `4px solid ${matrixConfig?.color}`
          }}>
            {/* Matrix Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>{matrixConfig?.icon}</span>
                  {currentMatrix.name}
                </h2>
                <p style={{ color: '#6b7280', margin: 0 }}>{currentMatrix.description}</p>
              </div>
              
              {editingMatrix ? (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button variant="outline" onClick={cancelEditing}>
                    <X size={16} /> Cancel
                  </Button>
                  <Button onClick={saveMatrix} style={{ background: matrixConfig?.color }}>
                    <Save size={16} /> Save Changes
                  </Button>
                </div>
              ) : (
                <Button onClick={startEditing} style={{ background: matrixConfig?.color }}>
                  <Edit2 size={16} /> Edit Matrix
                </Button>
              )}
            </div>

            {/* Pricing Tiers */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                Pricing Tiers
              </h3>
              {renderPriceTable(currentMatrix, !!editingMatrix)}
            </div>

            {/* Setup Fees */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                Setup Fees
              </h3>
              {renderSetupFees(currentMatrix, !!editingMatrix)}
            </div>

            {/* Additional Charges */}
            {renderAdditionalCharges(currentMatrix)}

            {/* Notes */}
            {currentMatrix.notes && (
              <div style={{ 
                marginTop: '24px', 
                padding: '16px', 
                background: '#f0f9ff', 
                borderRadius: '8px',
                borderLeft: `4px solid ${matrixConfig?.color}`
              }}>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>
                  <strong>Notes:</strong> {currentMatrix.notes}
                </p>
              </div>
            )}

            {/* Last Updated */}
            <p style={{ 
              marginTop: '24px', 
              fontSize: '12px', 
              color: '#9ca3af',
              textAlign: 'right'
            }}>
              Last updated: {new Date(currentMatrix.updated_at).toLocaleString()}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PriceMatrices;
